/**
 * Design Token 集中管理
 *
 * 所有色彩、字型、間距、圓角、陰影等 UI 常數均於此定義，
 * 其餘檔案（darkTheme, constants, monitoringStyles, 各元件）一律 import 此模組。
 */

// ─── 色彩 ─────────────────────────────────────────────────────────────────────

import { BOOT_THEME } from './uiTheme';

/** 橘暖風格（馬修預設，原本的暖赤陶＋米色）；介面風格切換見 uiTheme.ts */
const CLAUDE_COLORS = {
  /** 品牌主色 */
  primary: '#D4714E',
  primaryHover: 'rgba(212,113,78,0.1)',
  primarySubtle: 'rgba(212,113,78,0.06)',

  /** 文字 */
  text: '#2D2D2D',
  textSecondary: '#8B8580',

  /** 背景 */
  bgWhite: '#FFFFFF',
  bgLayout: '#F5F0E8',
  bgSider: '#F0EBE3',
  bgCard: '#F8F6F2',
  bgTableHeader: '#FAF7F2',
  bgSubtle: '#fafafa',
  bgInput: '#FAFAF8',

  /** 邊框 / 分隔線 */
  border: '#E5DDD3',
  borderLight: '#f0ede8',

  /** 狀態色 */
  statusOnline: '#52c41a',
  statusOffline: '#ff4d4f',
  statusWarning: '#faad14',
  statusNormal: '#1890ff',

  /** 嚴重度 */
  severityCritical: '#ff4d4f',
  severityAlarm: '#fa8c16',
  severityWarning: '#1890ff',
  severityInfo: '#52c41a',

  /** EHI 健康指數 */
  ehiGood: '#52c41a',
  ehiFair: '#faad14',
  ehiPoor: '#ff7a45',
  ehiCritical: '#ff4d4f',
  ehiNoData: '#C0B8AE',

  /** 系統圖色彩 */
  sysRun: '#4CAF50',
  sysStop: '#9E9E9E',
  sysAlarm: '#F44336',
  sysDefrost: '#2196F3',
  sysShutdown: '#E65100',
  sysPipe: '#64B5F6',
  sysPipeOff: '#BDBDBD',

  /** 迴路辨識色（Widget 用） */
  circuit1: '#1890ff',
  circuit2: '#52c41a',
};

export type ColorTokens = typeof CLAUDE_COLORS;

// 東海 VIS 風格：只換品牌與中性色，狀態／嚴重度色各風格相同
const VIS_OVERRIDES: Partial<ColorTokens> = {
  primary: '#00498A',
  primaryHover: 'rgba(0,73,138,0.08)',
  primarySubtle: 'rgba(0,73,138,0.05)',
  text: '#1F2937',
  textSecondary: '#6B7280',
  bgLayout: '#F5F6F8',
  bgSider: '#FFFFFF',
  bgCard: '#FAFBFC',
  bgTableHeader: '#F5F6F8',
  bgInput: '#FAFBFC',
  border: '#E9EAEA',
  borderLight: '#F0F1F3',
};

// 深色：底色由深到淺＝layout < card/container < 浮層；狀態／嚴重度色沿用（深底上一樣醒目）
const DARK_OVERRIDES: Partial<ColorTokens> = {
  primary: '#4C9BE8',
  primaryHover: 'rgba(76,155,232,0.16)',
  primarySubtle: 'rgba(76,155,232,0.08)',
  text: '#E5E7EB',
  textSecondary: '#9CA3AF',
  bgWhite: '#1B1F29',
  bgLayout: '#151922',
  bgSider: '#1B1F29',
  bgCard: '#1E232D',
  bgTableHeader: '#232833',
  bgSubtle: '#1E232D',
  bgInput: '#1E232D',
  border: '#343A46',
  borderLight: '#2A2F3A',
  ehiNoData: '#4B5260',
  sysPipeOff: '#4B5260',
};

/** 目前風格的色票（開機時依 BOOT_THEME 決定；切換風格會重新載入頁面） */
export const colors: ColorTokens =
  BOOT_THEME === 'vis' ? { ...CLAUDE_COLORS, ...VIS_OVERRIDES }
    : BOOT_THEME === 'dark' ? { ...CLAUDE_COLORS, ...DARK_OVERRIDES }
      : CLAUDE_COLORS;
export const IS_DARK = BOOT_THEME === 'dark';

/** 圖表折線色序列（10 色） */
export const chartLineColors = [
  '#1890ff', '#52c41a', '#ff7a45', '#722ed1', '#eb2f96',
  '#13c2c2', '#faad14', '#2f54eb', '#a0d911', '#ff4d4f',
] as const;

/** Recharts 圖表元素色（網格、軸、Tooltip） */
export const chartTheme = {
  gridStroke: colors.border,
  axisTickColor: colors.textSecondary,
  tooltipBg: colors.bgWhite,
  tooltipBorder: colors.border,
  tooltipColor: colors.text,
  brushStroke: colors.primary,
  brushFill: colors.bgTableHeader,
  hintColor: colors.textSecondary,
} as const;

// ─── 字型 ─────────────────────────────────────────────────────────────────────

export const fontSize = {
  xs: 9,
  sm: 11,
  base: 12,
  md: 13,
  lg: 14,
  xl: 16,
  '2xl': 18,
  '3xl': 22,
  '4xl': 26,
  '5xl': 28,
  '6xl': 36,
} as const;

export const fontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const lineHeight = {
  tight: 1,
  compact: 1.2,
  snug: 1.3,
  normal: 1.6,
  relaxed: 1.8,
} as const;

// ─── 間距 ─────────────────────────────────────────────────────────────────────

export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 20,
  '3xl': 24,
  '4xl': 32,
} as const;

// ─── 圓角 ─────────────────────────────────────────────────────────────────────

export const radius = {
  sm: 4,
  base: 6,
  md: 8,
  lg: 10,
  xl: 12,
} as const;

// ─── 陰影 ─────────────────────────────────────────────────────────────────────

export const shadow = {
  subtle: '0 1px 3px rgba(0,0,0,0.06)',
  light: '0 2px 8px rgba(0,0,0,0.1)',
  medium: '0 4px 12px rgba(0,0,0,0.12)',
  warm: '0 4px 20px rgba(74,55,40,0.12)',
} as const;

// ─── 轉場動畫 ──────────────────────────────────────────────────────────────────

export const transition = {
  fast: '0.15s',
  normal: '0.3s',
} as const;
